import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, ChevronDown, Database, Folder, Lock, MessageSquare, Plus, Radar, Search, Sparkles, Users } from 'lucide-react';
import { AppChrome, toolLogo } from './AppChrome';

/**
 * The brain has three primitives, and abstract diagrams kept hiding them. So
 * this section shows the actual product: Tracks (the containers memory lives
 * in, and therefore the permission boundary), Scans (scheduled ingestion from
 * a connected tool into a track), and Observations (the timeline of what was
 * learned). A second, smaller block then shows a question being answered from
 * all three.
 */

const GIGA_MARK = 'https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/giga-mark.png';
type TabId = 'Tracks' | 'Scans' | 'Observations' | 'Ask';

/**
 * A numbered rail rather than a row of tabs: these four are a sequence, and the
 * taglines carry the thing the screens can't say — most importantly that
 * tracks are not something you sit down and design up front.
 */
const STEPS: Array<{
  id: TabId;
  n: string;
  label: string;
  blurb: string;
  icon: React.ElementType;
}> = [{
  id: 'Tracks',
  n: '01',
  label: 'Tracks hold memory',
  blurb: 'You don’t design these up front. A track appears as the work does — a customer, a project, a launch — and whoever can see it can use what it knows.',
  icon: Folder
}, {
  id: 'Scans',
  n: '02',
  label: 'Scans fill them',
  blurb: 'Each scan reads one connected tool on a cadence and files what’s new into a track. Set once, then it runs without anyone remembering to.',
  icon: Radar
}, {
  id: 'Observations',
  n: '03',
  label: 'Observations accumulate',
  blurb: 'Every fact learned, titled and sourced, on a timeline you can read and filter. Week 20 knows more than week 1 without anyone maintaining it.',
  icon: Sparkles
}, {
  id: 'Ask',
  n: '04',
  label: 'Ask a question',
  blurb: 'An answer draws on the tracks you can see, the observations inside them, and a live look at the connected tool.',
  icon: MessageSquare
}];
export function CompanyBrainPillar() {
  const [tab, setTab] = useState<TabId>('Tracks');
  return <div data-id="element-257" data-mp="21ukiwyotf1g2">
      <div className="mx-auto max-w-3xl text-center" data-id="element-258" data-mp="21uu84k21d4kp">
        <p className="mb-4 text-[12px] font-bold uppercase tracking-[0.14em] text-[#EA580C]" data-id="element-259" data-mp="26rax1gh27myu">
          Company Brain
        </p>
        <h2 className="font-title text-4xl font-bold leading-[1.05] tracking-tight text-[#2B211A] md:text-[56px]" data-id="element-260" data-mp="2ui6xf1zkzqhp">
          Memory with a
          <br data-id="element-261" data-mp="2krvea87btaq8" />
          structure you can see.
        </h2>
        <p className="mx-auto mt-5 max-w-[56ch] text-lg leading-relaxed text-[#6B5D52]" data-id="element-262" data-mp="2krc6vlfay4qg">
          Not a document dump. Four moving parts, all of them inspectable.
        </p>
      </div>

      <div className="mx-auto mt-10 grid max-w-[1120px] gap-5 lg:grid-cols-[240px_minmax(0,1fr)] lg:gap-6" data-id="element-263" data-mp="3dfrazcwwb2n0">
        <ol className="space-y-1.5" data-id="element-264" data-mp="3i8sq9m10l474">
          {STEPS.map(s => {
          const on = s.id === tab;
          return <li key={s.id} data-id="element-265" data-mp="3rk8fskv3odc4">
                <button type="button" onClick={() => setTab(s.id)} aria-pressed={on} className={`w-full rounded-2xl border px-3.5 py-3 text-left transition-colors ${on ? 'border-[#2B211A]/25 bg-white shadow-[0_18px_40px_-32px_rgba(43,33,26,0.5)]' : 'border-transparent hover:bg-white/60'}`} data-id="element-266" data-mp="3nkg1drj0zcyu">
                  <span className="flex items-center gap-2.5" data-id="element-267" data-mp="38n77aq2c7v3f">
                    <span className={`font-mono text-[11px] font-bold ${on ? 'text-[#EA580C]' : 'text-neutral-400'}`} data-id="element-268" data-mp="38npak6nf39ku">
                      {s.n}
                    </span>
                    <s.icon size={13} strokeWidth={2} className={on ? 'text-[#2B211A]' : 'text-neutral-400'} data-id="element-269" data-mp="3imejvb18zlph" />
                    <span className={`text-[14px] font-semibold ${on ? 'text-[#2B211A]' : 'text-[#6B5D52]'}`} data-id="element-270" data-mp="3dlihx30gi9mp">
                      {s.label}
                    </span>
                  </span>
                  <AnimatePresence initial={false} data-id="element-271" data-mp="suzvjmsyzuat">
                    {on && <motion.span initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.22,
                  ease: [0.23, 1, 0.32, 1]
                }} className="block overflow-hidden" data-id="element-272" data-mp="2ykbnuzrg5n6g">
                        <span className="mt-2 block text-[13px] leading-relaxed text-[#6B5D52]" data-id="element-273" data-mp="3hiegrpn4849j">
                          {s.blurb}
                        </span>
                      </motion.span>}
                  </AnimatePresence>
                </button>
              </li>;
        })}
        </ol>

        {/* Sidebar off: the numbered rail is already the left column, and the
         screens need the width back. */}
        <AppChrome sidebar={false} data-id="element-274" data-mp="4649s9y442c8">
          <AnimatePresence mode="wait" data-id="element-275" data-mp="934hsqeivf5z">
            <motion.div key={tab} initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.24,
            ease: [0.23, 1, 0.32, 1]
          }} className="px-5 py-5 md:px-6 md:py-6" data-id="element-276" data-mp="945vz3qu4hrx">
              {tab === 'Tracks' && <TracksScreen data-id="element-277" data-mp="1flx73p7v71rg" />}
              {tab === 'Scans' && <ScansScreen data-id="element-278" data-mp="1fn8dlwsp2nm9" />}
              {tab === 'Observations' && <ObservationsScreen data-id="element-279" data-mp="2ccfw0vy0ogly" />}
              {tab === 'Ask' && <AskScreen data-id="element-280" data-mp="2ce0qymo8u1kc" />}
            </motion.div>
          </AnimatePresence>
        </AppChrome>
      </div>
    </div>;
}
function ScreenHead({
  title,
  meta,
  action
}: {
  title: string;
  meta: string;
  action: string;
}) {
  return <div className="flex flex-wrap items-end justify-between gap-3" data-id="element-281" data-mp="1ykroz72mkibu">
      <div data-id="element-282" data-mp="1yioyklxk8n6l">
        <h3 className="font-title text-[22px] font-medium tracking-tight text-[#2B211A]" data-id="element-283" data-mp="2vzx5gvr2ajcl">
          {title}
        </h3>
        <p className="mt-1 text-[13px] text-neutral-500" data-id="element-284" data-mp="2r8nffu1lpe06">{meta}</p>
      </div>
      <span className="inline-flex items-center gap-1.5 rounded-lg border border-[#E7E0D4] px-3 py-1.5 text-[12.5px] font-bold text-[#2B211A]" data-id="element-285" data-mp="3a5fj01it84g9">
        <Plus size={13} data-id="element-286" data-mp="3f37bawnd9skz" />
        {action}
      </span>
    </div>;
}

/* ── Tracks ─────────────────────────────────────────────────────────── */

const TRACKS = [{
  name: 'Northwind Labs',
  group: 'Giga Master',
  shared: true,
  members: ['SC', 'PR', 'TO'],
  observations: 412,
  files: 9,
  scans: 3,
  updated: 'just now',
  files_list: [{
    path: 'customers/northwind.md',
    note: 'Timeline, champion, budget cycle'
  }, {
    path: 'customers/northwind-pilot.md',
    note: 'Pilot scope, success criteria'
  }]
}, {
  name: 'Onboarding revamp',
  group: 'Giga Master',
  shared: true,
  members: ['SC', 'ML'],
  observations: 268,
  files: 6,
  scans: 2,
  updated: '12 min ago',
  files_list: [{
    path: 'product/onboarding.md',
    note: 'What we promise, where people get stuck'
  }, {
    path: 'product/setup-blockers.md',
    note: 'Recurring blockers by frequency'
  }]
}, {
  name: 'Q3 pipeline',
  group: 'Giga Marketing',
  shared: true,
  members: ['PR', 'SC'],
  observations: 631,
  files: 11,
  scans: 4,
  updated: '1 hour ago',
  files_list: [{
    path: 'accounts/renewals.md',
    note: 'Every renewal, owner, last contact'
  }, {
    path: 'commercial/pricing-rules.md',
    note: 'Ceilings and who can approve them'
  }]
}, {
  name: 'Board reporting',
  group: 'Faith’s Private Space',
  shared: false,
  members: ['FA'],
  observations: 88,
  files: 4,
  scans: 1,
  updated: 'Yesterday',
  files_list: [{
    path: 'board/metrics-narrative.md',
    note: 'How we explain each metric'
  }]
}];
function TracksScreen() {
  const [index, setIndex] = useState(0);
  const track = TRACKS[index];
  return <div data-id="element-287" data-mp="2su0if0rvlsd2">
      <ScreenHead title="Tracks" meta={`${TRACKS.length} tracks · memory lives inside one of these`} action="New track" data-id="element-288" data-mp="2suadv3vowrq5" />

      <div className="mt-5 grid gap-4 md:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]" data-id="element-289" data-mp="yilt2l2s1uxu">
        <ul className="space-y-2" data-id="element-290" data-mp="dmebw8zlw68n">
          {TRACKS.map((t, i) => <li key={t.name} data-id="element-291" data-mp="dlwcj3hndbwi" data-mpi={i}>
              <button type="button" onClick={() => setIndex(i)} aria-pressed={i === index} className={`flex w-full items-center gap-3 rounded-2xl border px-3.5 py-3 text-left transition-colors ${i === index ? 'border-[#EA580C]/45 bg-[#FFF8F3]' : 'border-[#EFEAE1] hover:border-[#2B211A]/20'}`} data-id="element-292" data-mp="hnzt26j6b6on">
                <Folder size={15} strokeWidth={1.9} className="shrink-0 text-neutral-400" data-id="element-293" data-mp="rfndvol85ph5" />
                <span className="min-w-0 flex-1" data-id="element-294" data-mp="3qvalkuusyp5d">
                  <span className="block truncate text-[13.5px] font-semibold text-[#2B211A]" data-id="element-295" data-mp="3uvkr66eondjp">
                    {t.name}
                  </span>
                  <span className="mt-0.5 flex items-center gap-1.5 truncate text-[11.5px] text-neutral-500" data-id="element-296" data-mp="3ql04rtpogrx">
                    {t.shared ? <Users size={10} data-id="element-297" data-mp="1f3ndle0ikq7v" /> : <Lock size={10} data-id="element-298" data-mp="1f46poe3hgkdz" />}
                    {t.group}
                  </span>
                </span>
                <span className="shrink-0 text-right text-[11px] text-neutral-400" data-id="element-299" data-mp="1k16rgctav0za">
                  {t.observations}
                  <span className="block" data-id="element-300" data-mp="34jr1rwbkt489">observations</span>
                </span>
              </button>
            </li>)}
        </ul>

        <div className="rounded-2xl border border-[#EFEAE1] p-4" data-id="element-301" data-mp="2uufwylvq2lq6">
          <div className="flex items-start justify-between gap-3" data-id="element-302" data-mp="2uswcsk6y3qj4">
            <div data-id="element-303" data-mp="2pul8sq3c0a8t">
              <p className="font-title text-[16px] font-bold tracking-tight text-[#2B211A]" data-id="element-304" data-mp="2pv39byx11amj">
                {track.name}
              </p>
              <p className="mt-0.5 text-[11.5px] text-neutral-500" data-id="element-305" data-mp="2guy680yv8ye6">
                Updated {track.updated}
              </p>
            </div>
            <span className="flex -space-x-1.5" data-id="element-306" data-mp="2bwmshf5ga3qo">
              {track.members.map(m => <span key={m} className="flex size-6 items-center justify-center rounded-full bg-[#2B211A] text-[9px] font-bold text-white ring-2 ring-white" data-id="element-307" data-mp="26xa5muekwwo3">
                  {m}
                </span>)}
            </span>
          </div>

          {/* The point of the whole screen: access is a property of the track,
           not of each individual memory. */}
          <div className="mt-3.5 flex items-start gap-2.5 rounded-xl bg-[#F6F2EA] px-3 py-2.5" data-id="element-308" data-mp="1k4vn9ij81k48">
            {track.shared ? <Users size={13} className="mt-0.5 shrink-0 text-[#6B5D52]" data-id="element-309" data-mp="1u18du7jgzhur" /> : <Lock size={13} className="mt-0.5 shrink-0 text-[#6B5D52]" data-id="element-310" data-mp="1u0gwa0mpjknh" />}
            <p className="text-[12.5px] leading-relaxed text-[#4A3E35]" data-id="element-311" data-mp="1p36ynxp602ok">
              {track.shared ? <>
                  Anyone in <strong className="font-semibold" data-id="element-312" data-mp="11d2xry3d2h19">{track.group}</strong>{' '}
                  can use this memory. Add someone to the group and they inherit
                  it — no per-file sharing.
                </> : <>
                  Private to <strong className="font-semibold" data-id="element-313" data-mp="16bntbk7o4cjd">{track.group}</strong>
                  . Nothing here reaches other people's answers.
                </>}
            </p>
          </div>

          <dl className="mt-3.5 grid grid-cols-3 gap-2 text-center" data-id="element-314" data-mp="ijqle47ca4nj">
            {[['Observations', track.observations], ['Memory files', track.files], ['Scans', track.scans]].map(([label, value]) => <div key={label as string} className="rounded-xl border border-[#EFEAE1] py-2" data-id="element-315" data-mp="2p892nd8tc9pb">
                <dt className="text-[10.5px] font-semibold uppercase tracking-[0.06em] text-neutral-400" data-id="element-316" data-mp="2u35577boefgo">
                  {label}
                </dt>
                <dd className="font-title text-[17px] font-bold text-[#2B211A]" data-id="element-317" data-mp="2kahdgqzjz1ot">
                  {value}
                </dd>
              </div>)}
          </dl>

          <p className="mt-3.5 text-[10.5px] font-bold uppercase tracking-[0.08em] text-neutral-400" data-id="element-318" data-mp="3czwlr8zu2gh1">
            Memory files
          </p>
          <ul className="mt-1.5 space-y-1.5" data-id="element-319" data-mp="2yz3paipz1bba">
            {track.files_list.map(f => <li key={f.path} className="rounded-xl border border-[#EFEAE1] px-3 py-2" data-id="element-320" data-mp="331gun60t377j">
                <span className="block truncate font-mono text-[12px] font-semibold text-[#2B211A]" data-id="element-321" data-mp="1nkzz7nmi87bi">
                  {f.path}
                </span>
                <span className="mt-0.5 block truncate text-[11.5px] text-[#6B5D52]" data-id="element-322" data-mp="1ro4xtf3grouv">
                  {f.note}
                </span>
              </li>)}
          </ul>
        </div>
      </div>
    </div>;
}

/* ── Scans ──────────────────────────────────────────────────────────── */

const SCANS = [{
  logo: 'granola',
  name: 'Meeting notes',
  source: 'Granola',
  scope: 'Calls mentioning a named account',
  cadence: 'Every hour',
  track: 'Northwind Labs',
  last: '6 min ago',
  next: 'in 54 min',
  kept: 3,
  health: [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1]
}, {
  logo: 'gmail',
  name: 'Inbox sweep',
  source: 'Gmail',
  scope: 'Threads with customers, unanswered 5+ days',
  cadence: 'Every hour',
  track: 'Q3 pipeline',
  last: '21 min ago',
  next: 'in 39 min',
  kept: 4,
  health: [1, 1, 1, 1, 0, 1, 1, 1, 1, 1, 1, 1]
}, {
  logo: 'salesforce',
  name: 'Pipeline changes',
  source: 'Salesforce',
  scope: 'Stage, amount and close-date moves',
  cadence: 'Every hour',
  track: 'Q3 pipeline',
  last: '3 min ago',
  next: 'in 57 min',
  kept: 7,
  health: [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2]
}, {
  logo: 'intercom',
  name: 'Support themes',
  source: 'Intercom',
  scope: 'New conversations, grouped by cause',
  cadence: 'Every 4 hours',
  track: 'Onboarding revamp',
  last: '1 hour ago',
  next: 'in 3 hours',
  kept: 2,
  health: [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1]
}, {
  logo: 'linear',
  name: 'Shipped work',
  source: 'Linear',
  scope: 'Issues moved to Done',
  cadence: 'Daily',
  track: 'Onboarding revamp',
  last: 'Today, 09:00',
  next: 'Tomorrow, 09:00',
  kept: 5,
  health: [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1]
}];
function ScansScreen() {
  return <div data-id="element-323" data-mp="tbwudvi0nsya">
      <ScreenHead title="Scans" meta="5 scans · pulling from connected tools on a schedule" action="New scan" data-id="element-324" data-mp="tc6mppqjac75" />

      <ul className="mt-5 space-y-2" data-id="element-325" data-mp="ydm6zfkw0eoh">
        {SCANS.map(s => <li key={s.name} className="flex flex-wrap items-center gap-3 rounded-2xl border border-[#EFEAE1] px-3.5 py-3" data-id="element-326" data-mp="24smsssg77ghc">
            <img src={toolLogo(s.logo)} alt="" aria-hidden="true" className="size-[18px] shrink-0 object-contain" data-id="element-327" data-mp="29q57kzgwcpx9" />
            <span className="min-w-0 flex-1" data-id="element-328" data-mp="3ioogry0fiugk">
              <span className="block truncate text-[13.5px] font-semibold text-[#2B211A]" data-id="element-329" data-mp="3dpbjejbxm4zq">
                {s.name}
                <span className="ml-1.5 font-normal text-neutral-400" data-id="element-330" data-mp="3rpcn06jkzqiw">
                  · {s.source}
                </span>
              </span>
              <span className="mt-0.5 block truncate text-[11.5px] text-neutral-500" data-id="element-331" data-mp="3mof4ttykmwhe">
                {s.scope}
              </span>
            </span>

            {/* Where it lands is the important column — a scan without a track
             would be memory nobody can govern. */}
            <span className="hidden shrink-0 items-center gap-1.5 rounded-full bg-[#F6F2EA] px-2.5 py-1 text-[11.5px] font-semibold text-[#4A3E35] sm:inline-flex" data-id="element-332" data-mp="e9exemqo8mln">
              <Folder size={10} strokeWidth={2} data-id="element-333" data-mp="o7kyx38kfqpz" />
              {s.track}
            </span>

            <span className="hidden shrink-0 items-end gap-[3px] md:flex" data-id="element-334" data-mp="j9jfuib53rei">
              {s.health.map((h, i) => <span key={i} className={`h-3.5 w-[3px] rounded-full ${h === 1 ? 'bg-emerald-400' : h === 0 ? 'bg-red-400' : 'bg-amber-400'}`} data-id="element-335" data-mp="12u9w2yfchrt7" data-mpi={i} />)}
            </span>

            <span className="shrink-0 text-right" data-id="element-336" data-mp="17rtiwmq3zcwr">
              <span className="block text-[11.5px] font-medium text-[#4A3E35]" data-id="element-337" data-mp="sxf12pia69jo">
                {s.cadence}
              </span>
              <span className="block text-[11px] text-neutral-400" data-id="element-338" data-mp="xu5fz9icmz3i">
                ran {s.last} · next {s.next}
              </span>
            </span>
          </li>)}
      </ul>

      <p className="mt-4 border-t border-[#EFEAE1] pt-3.5 text-[12.5px] leading-relaxed text-neutral-500" data-id="element-339" data-mp="ow3eytym86g8">
        Every run keeps only what is new and worth remembering — the last sweep
        of these five kept 21 observations out of several hundred records read.
      </p>
    </div>;
}

/* ── Observations ───────────────────────────────────────────────────── */

const TIMELINE: Array<{
  date: string;
  rows: Array<{
    time: string;
    text: string;
    kind: 'observation' | 'memory';
  }>;
}> = [{
  date: 'Tue, August 11, 2026',
  rows: [{
    time: '10:32',
    text: 'Northwind pushed the pilot to Q4 — budget freeze on new spend, champion unchanged',
    kind: 'observation'
  }, {
    time: '09:14',
    text: 'Acme moved to Legal review · $84k · closes 12 Sep',
    kind: 'memory'
  }]
}, {
  date: 'Mon, August 10, 2026',
  rows: [{
    time: '16:41',
    text: 'Setup named as the top blocker in 31 support conversations this month',
    kind: 'observation'
  }, {
    time: '15:08',
    text: 'CSV import bug shipped — clears the blocker cited on two open deals',
    kind: 'memory'
  }, {
    time: '11:55',
    text: 'Onboarding is quoted to customers as 14 days, not 30',
    kind: 'observation'
  }]
}, {
  date: 'Fri, August 7, 2026',
  rows: [{
    time: '17:02',
    text: 'Four renewal threads went quiet for more than a week: Acme, Vertex, Loop, Harbour',
    kind: 'observation'
  }, {
    time: '14:20',
    text: 'Discount ceiling agreed at 15% — supersedes the case-by-case rule',
    kind: 'memory'
  }, {
    time: '10:36',
    text: 'Invoice 4482 is 21 days overdue and has had no follow-up',
    kind: 'observation'
  }]
}];
const FILTERS = ['All tracks', 'All sources', 'Last 30 days'];
function ObservationsScreen() {
  const [mode, setMode] = useState<'Human' | 'Heartbeat'>('Human');
  return <div data-id="element-340" data-mp="3qtpb1sql4wha">
      <ScreenHead title="Observations" meta="4,128 kept · newest first" action="Add observation" data-id="element-341" data-mp="3qu8r13wodaso" />

      <div className="mt-5 flex flex-wrap items-center gap-2" data-id="element-342" data-mp="3vttpzebuhj5e">
        {FILTERS.map(f => <span key={f} className="inline-flex items-center gap-1.5 rounded-lg border border-[#E7E0D4] px-2.5 py-1.5 text-[12px] font-semibold text-[#4A3E35]" data-id="element-343" data-mp="4jcx7gv637os">
            {f}
            <ChevronDown size={12} className="text-neutral-400" data-id="element-344" data-mp="dkkubvly62ha" />
          </span>)}
        <span className="ml-auto inline-flex items-center gap-2 rounded-lg bg-white px-2.5 py-1.5 ring-1 ring-[#EFEAE1]" data-id="element-345" data-mp="2aexrm5bf2zk4">
          <Search size={12} className="text-neutral-300" data-id="element-346" data-mp="2fe0o31dbzxgh" />
          <span className="text-[12px] text-neutral-400" data-id="element-347" data-mp="l553zeoowqsp">
            Search observations
          </span>
        </span>
        {/* Human vs Heartbeat: what a person said versus what a scan noticed. */}
        <span className="inline-flex rounded-lg border border-[#E7E0D4] p-0.5" data-id="element-348" data-mp="uvq0mwkszl8z">
          {(['Human', 'Heartbeat'] as const).map(m => <button key={m} type="button" onClick={() => setMode(m)} aria-pressed={mode === m} className={`rounded-[6px] px-2.5 py-1 text-[12px] font-semibold transition-colors ${mode === m ? 'bg-[#2B211A] text-white' : 'text-[#6B5D52]'}`} data-id="element-349" data-mp="pxescqa5pisv">
              {m}
            </button>)}
        </span>
      </div>

      <div className="mt-4" data-id="element-350" data-mp="c5i3cv3sbp0p">
        {TIMELINE.map(day => <div key={day.date} className="mb-4 last:mb-0" data-id="element-351" data-mp="75m2h4qdwdpd">
            <p className="text-[10.5px] font-bold uppercase tracking-[0.1em] text-neutral-400" data-id="element-352" data-mp="7417je05qsqz">
              {day.date}
            </p>
            <ul className="mt-1.5 border-t border-[#EFEAE1]" data-id="element-353" data-mp="1ikrfb1k8bsmp">
              {day.rows.map(r => <li key={r.text} className="flex items-center gap-3 border-b border-[#EFEAE1] py-2.5" data-id="element-354" data-mp="1in58t5z9gws5">
                  <span className="w-[38px] shrink-0 font-mono text-[11.5px] text-neutral-400" data-id="element-355" data-mp="30kqpjh8osmxk">
                    {r.time}
                  </span>
                  <span className="min-w-0 flex-1 text-[13.5px] leading-snug text-[#2B211A]" data-id="element-356" data-mp="2rir8ioxo9ium">
                    {r.text}
                  </span>
                  <span className={`flex size-6 shrink-0 items-center justify-center rounded-md ${r.kind === 'observation' ? 'bg-indigo-50 text-indigo-500' : 'bg-emerald-50 text-emerald-600'}`} title={r.kind === 'observation' ? 'Observation' : 'Written to a memory file'} data-id="element-357" data-mp="2wincwpaa7cap">
                    {r.kind === 'observation' ? <Sparkles size={12} data-id="element-358" data-mp="3aer1a1yh4qf9" /> : <Database size={12} data-id="element-359" data-mp="3ff50qvm0es1o" />}
                  </span>
                </li>)}
            </ul>
          </div>)}
      </div>

      <p className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11.5px] text-neutral-500" data-id="element-360" data-mp="6z38wtb7ejbj">
        <span className="inline-flex items-center gap-1.5" data-id="element-361" data-mp="brd2cjbj21ik">
          <Sparkles size={11} className="text-indigo-500" data-id="element-362" data-mp="rzkfksa9pd1s" />
          Observation — a titled fact with its source
        </span>
        <span className="inline-flex items-center gap-1.5" data-id="element-363" data-mp="wy45t4vw9azy">
          <Database size={11} className="text-emerald-600" data-id="element-364" data-mp="j80momsb9xeq" />
          Written into a memory file
        </span>
      </p>
    </div>;
}

/* ── Step 04 · Ask ──────────────────────────────────────────────────── */

const ANSWER = {
  who: 'Priya · Growth',
  initials: 'PR',
  tint: '#7C2D12',
  question: 'Where did Northwind actually land?',
  /* Each line names the primitive from the three screens above it, so the
     answer is visibly assembled rather than conjured. */
  trace: [{
    icon: Folder,
    label: 'Track',
    text: 'Northwind Labs — you have access via Giga Master'
  }, {
    icon: Sparkles,
    label: 'Observations',
    text: '2 matched, from the pilot review and #sales'
  }, {
    icon: Database,
    label: 'Memory file',
    text: 'customers/northwind.md'
  }, {
    icon: Radar,
    label: 'Live read',
    text: 'Salesforce · opportunity still open, $84k, Legal review'
  }],
  answer: ['Pushed to Q4, not lost. Their CFO froze new spend on the 14th and the champion is unchanged — Dana asked to be re-engaged in September.', 'The opportunity is still open at $84k in Legal review, so the forecast should move rather than close.']
};
function AskScreen() {
  return <div className="flex min-h-[372px] flex-col" data-id="element-365" data-mp="6p50umbswmhx">
      <ScreenHead title="Ask" meta="Answers assembled from tracks, observations and a live read" action="New chat" data-id="element-366" data-mp="6ntv4gysp4o6" />

      <div className="mt-5 flex-1" data-id="element-367" data-mp="1pses3sr7jge">
        <div className="flex justify-end" data-id="element-368" data-mp="fpl1lo15tfj3">
          <p className="max-w-[80%] rounded-2xl rounded-br-md bg-[#F4F0E9] px-4 py-2.5 text-[14px] font-medium text-[#2B211A]" data-id="element-369" data-mp="fojrpmvvqz2y">
            {ANSWER.question}
          </p>
        </div>
        <p className="mt-1.5 flex items-center justify-end gap-1.5 text-[11px] text-neutral-400" data-id="element-370" data-mp="2hi98q8gxcn45">
          <span className="flex size-4 items-center justify-center rounded-full text-[7.5px] font-bold text-white" style={{
          backgroundColor: ANSWER.tint
        }} data-id="element-371" data-mp="2cj6elj2hg1ay">
            {ANSWER.initials}
          </span>
          {ANSWER.who}
        </p>

        <ul className="mt-5 space-y-1.5 rounded-2xl border border-[#EFEAE1] px-3.5 py-3" data-id="element-372" data-mp="qd5zzcnbw452">
          {ANSWER.trace.map((t, i) => <motion.li key={t.text} initial={{
          opacity: 0,
          x: -6
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.24,
          delay: 0.06 + i * 0.07,
          ease: [0.23, 1, 0.32, 1]
        }} className="flex items-center gap-2 text-[12.5px]" data-id="element-373" data-mp="uf9de6tk5isz" data-mpi={i}>
              <Check size={12} className="shrink-0 text-emerald-600" data-id="element-374" data-mp="194p4xp0wxs9q" />
              <t.icon size={12} className="shrink-0 text-neutral-400" data-id="element-375" data-mp="1e38pbkzuyvzz" />
              <span className="shrink-0 font-semibold text-[#2B211A]" data-id="element-376" data-mp="1e4jtvn14o7s5">
                {t.label}
              </span>
              <span className="min-w-0 truncate text-[#6B5D52]" data-id="element-377" data-mp="1n35j2113ul1w">{t.text}</span>
            </motion.li>)}
        </ul>

        <div className="mt-4 flex items-start gap-3" data-id="element-378" data-mp="37lzbyn23jt6j">
          <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-[#EA580C]" data-id="element-379" data-mp="37jd3ml7dt9n9">
            <img src={GIGA_MARK} alt="" className="size-3.5 [filter:brightness(0)_invert(1)]" data-id="element-380" data-mp="32opnb9mj7ljr" />
          </span>
          <div className="min-w-0 space-y-2.5" data-id="element-381" data-mp="2tlohzb5hpyh5">
            {ANSWER.answer.map(p => <p key={p} className="text-[14.5px] leading-relaxed text-[#3D3229]" data-id="element-382" data-mp="2tnj24n8xtmk6">
                {p}
              </p>)}
          </div>
        </div>
      </div>

      <div className="mt-5 flex items-center gap-2 rounded-full border border-[#E7E0D4] px-4 py-2" data-id="element-383" data-mp="3ma3wlwkpmery">
        <span className="flex-1 text-[13px] text-neutral-400" data-id="element-384" data-mp="3hils4yjh9w28">
          Ask anything about your company
        </span>
        <span className="flex size-6 items-center justify-center rounded-full bg-[#2B211A] text-white" data-id="element-385" data-mp="3ck12tu5rkwsk">
          <svg width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden="true" data-id="element-386" data-mp="dw3op2kjxxlj">
            <path d="M6 10V2M6 2L2.5 5.5M6 2l3.5 3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" data-id="element-387" data-mp="9294zz4y5img" />
          </svg>
        </span>
      </div>
    </div>;
}